import React, { useState, useEffect } from 'react'
import Topbar from '../components/Layout/Topbar'
import { Sidebar } from '../components/Layout/Sidebar'
import PickupPage from '../components/RequestPickup/PickupPage'
import { useSelector } from 'react-redux'
import UpdateRequestModal from '../components/RequestPickup/UpdateRequestModal'
import { supabase, getUsersByRoles } from '../supabase.config'
import { createUserNotifications } from '../utils/notificationService'

const RequestPickup = () => {
  const [pickupData, setPickupData] = useState({
    packages: '',
    date: '',
    time: ''
  })
  const [status, setStatus] = useState('')
  const [pickupId, setPickupId] = useState('')
  const [warehouseLocation, setWarehouseLocation] = useState({})
  const [requestId, setRequestId] = useState('')
  const [updateModal, setUpdateModal] = useState(false)
  const handleUpdateModal = () => setUpdateModal(!updateModal)

  const [warehouseList, setWarehouseList] = useState([])
  const [deliveryBoys, setDeliveryBoys] = useState([])
  const [deliveryBoy, setDeliveryBoy] = useState({})
  const { isAuthenticated, users, userProfile } = useSelector((state) => state.user)
  const [pickupList, setPickupList] = useState([])
  const actorUserId = userProfile?.id || userProfile?.uid || users || null

  const handlePickup = async () => {
    const now = new Date()
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const newRequestId = 'GRCR' + now.getFullYear() + (pickupList.length + 1)

      const { error } = await supabase
        .from('request_pickups')
        .insert([
          {
            request_id: newRequestId,
            client_id: user.id,
            client_name: userProfile?.name || null,
            requested_by: user.id,
            warehouse_id: warehouseLocation?.id || null,
            location: warehouseLocation || {},
            packages: Number(pickupData.packages) || 0,
            pickup_date: pickupData.date || null,
            pickup_time: pickupData.time || null,
            pickup_status: 'pending'
          }
        ])

      if (error) throw error

      const adminUsers = await getUsersByRoles(['admin'], 'logistics')
      await createUserNotifications({
        recipientUserIds: [
          ...(adminUsers?.data || []).map((row) => row.id),
          user.id,
        ],
        actorUserId,
        eventType: 'pickup_requested',
        title: 'Pickup requested',
        message: `Pickup request ${newRequestId} was created for ${Number(pickupData.packages) || 0} package(s).`,
        entityType: 'pickup_request',
        metadata: {
          requestId: newRequestId,
          warehouseId: warehouseLocation?.id || null,
        },
      })

      setPickupData({ packages: '', date: '', time: '' })
      setWarehouseLocation({})
      window.location.reload()
    } catch (e) {
      console.error('Failed to create pickup request', e)
      alert(e.message)
    }
  }

  const handleUpdateRequest = async () => {
    if (!pickupId) return
    try {
      const updateData =
        status !== 'pickingUp'
          ? { pickup_status: status }
          : {
              pickup_status: status,
              assigned_delivery_boy: deliveryBoy || null,
              assigned_delivery_boy_id: deliveryBoy?.id || null,
              delivery_boy_status: 'incomplete'
            }

      const { error } = await supabase
        .from('request_pickups')
        .update(updateData)
        .eq('id', pickupId)

      if (error) throw error

      await createUserNotifications({
        recipientUserIds: [
          deliveryBoy?.id,
          pickupList.find((item) => item.id === pickupId)?.user,
        ],
        actorUserId,
        eventType: 'pickup_status_updated',
        title: 'Pickup request updated',
        message: `Pickup request ${requestId || pickupId} moved to ${status}.`,
        entityType: 'pickup_request',
        metadata: {
          requestId: requestId || pickupId,
          status,
        },
      })
      handleUpdateModal()
    } catch (e) {
      console.error('Failed to update pickup request', e)
      alert(e.message)
    }
  }

  const fetchPickupList = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()

      let query = supabase
        .from('request_pickups')
        .select('*')
        .order('created_at', { ascending: false })

      if (userProfile?.role !== 'admin' && user) {
        query = query.eq('client_id', user.id)
      }

      const { data, error } = await query
      if (error) throw error

      const mapped = (data || []).map((row) => ({
        id: row.id,
        requestId: row.request_id,
        location: row.location || {},
        packages: row.packages,
        date: row.pickup_date,
        time: row.pickup_time,
        user: row.client_id,
        userName: row.client_name,
        pickupBoy: row.assigned_delivery_boy || {},
        pickupPerson: row.assigned_delivery_boy_id || null,
        deliveryBoyStatus: row.delivery_boy_status || null,
        pickupStatus: row.pickup_status
      }))
      setPickupList(mapped)
    } catch (e) {
      console.error('Failed to fetch pickup requests', e)
    }
  }

  const fetchPickupDetails = async () => {
    if (!requestId) return
    try {
      const { data, error } = await supabase
        .from('request_pickups')
        .select('pickup_status')
        .eq('request_id', requestId)
        .limit(1)
        .maybeSingle()
      if (error) throw error
      if (data) setStatus(data.pickup_status)
    } catch (e) {
      console.error('Failed to fetch pickup details', e)
    }
  }

  const fetchWarehouse = async () => {
    try {
      const { data, error } = await supabase
        .from('warehouses')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw error

      const mapped = (data || []).map((w) => ({
        id: w.id,
        pickupName: { name: w.name, companyName: w.company_name || '' },
        pinCode: w.pin_code || '',
        city: w.city || '',
        state: w.state || '',
        country: w.country || '',
        address: w.address || '',
        personName: w.contact_person || '',
        email: '',
        phone: w.contact_number || '',
        slot: w.preferred_slot || ''
      }))
      setWarehouseList(mapped)
    } catch (e) {
      console.error('Failed to fetch warehouses', e)
    }
  }

  const fetchDeliveryBoy = async () => {
    try {
      // Prefer both roles for logistics service
      let usersRows = null
      try {
        const res = await supabase
          .from('users')
          .select('id, name, email, contact, role, service')
          .in('role', ['Delivery Boy', 'Delivery Partner'])
          .eq('service', 'logistics')
          .order('created_at', { ascending: false })
        if (res.error) throw res.error
        usersRows = res.data
      } catch (_) {
        // Fallback to helper if direct query fails for any reason
        try {
          const res2 = await getUsersByRoles(['Delivery Boy','Delivery Partner'], 'logistics')
          usersRows = res2.data || []
        } catch {
          usersRows = []
        }
      }

      const mapped = (usersRows || []).map((u) => ({ id: u.id, name: u.name, email: u.email, phone: u.contact }))
      setDeliveryBoys(mapped)
    } catch (e) {
      console.error('Failed to fetch delivery boys', e)
      setDeliveryBoys([])
    }
  }

  const deletePickup = async (id) => {
    try {
      const { error } = await supabase.from('request_pickups').delete().eq('id', id)
      if (error) throw error
      window.location.reload()
    } catch (e) {
      console.error('Failed to delete pickup', e)
      alert(e.message)
    }
  }

  useEffect(() => {
    fetchPickupList()
    fetchWarehouse()
    fetchDeliveryBoy()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchPickupDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId])

  // Ensure delivery boys are loaded when selecting 'pickingUp' and opening the modal
  useEffect(() => {
    if (updateModal && status === 'pickingUp') {
      fetchDeliveryBoy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateModal, status])

  return (
    <div className='flex bg-slate-100/80'>
      <UpdateRequestModal
        deliveryBoy={deliveryBoy}
        setDeliveryBoy={setDeliveryBoy}
        deliveryBoys={deliveryBoys}
        setDeliveryBoys={setDeliveryBoys}
        status={status}
        setStatus={setStatus}
        updateModal={updateModal}
        setUpdateModal={setUpdateModal}
        handleUpdateModal={handleUpdateModal}
        handleUpdateRequest={handleUpdateRequest}
      />
      <Sidebar />
      <div className='flex h-[100vh] flex-1 flex-col overflow-y-scroll'>
        <Topbar />
        <PickupPage
          deletePickup={deletePickup}
          requestId={requestId}
          setRequestId={setRequestId}
          pickupId={pickupId}
          setPickupId={setPickupId}
          handleUpdateModal={handleUpdateModal}
          warehouseList={warehouseList}
          warehouseLocation={warehouseLocation}
          setWarehouseLocation={setWarehouseLocation}
          pickupData={pickupData}
          setPickupData={setPickupData}
          handlePickup={handlePickup}
          pickupList={pickupList}
        />
      </div>
    </div>
  )
}

export default RequestPickup
